import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { addDays, addMonths, endOfMonth, endOfWeek, format, isSameMonth, startOfMonth, startOfWeek } from "date-fns";
import { useFetch } from "@/lib/api";
import { fmtDate, toISODate, toneClass, APPT_STATUSES } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { PageHeader, Loading, ErrorBox, StatusBadge, DemoTag, NativeSelect } from "@/components/Common";
import { SimpleTable } from "@/components/SimpleTable";
import { AppointmentDialog } from "@/components/AppointmentDialog";

export function Appointments() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState(params.get("status") || "");
  const [open, setOpen] = useState(null);
  const { data, loading, error, reload } = useFetch(`/admin/appointments?status=${encodeURIComponent(status)}`);
  return (
    <div>
      <PageHeader title="Appointments" subtitle="Requested appointments are pending until you confirm them." />
      <div className="mb-4 max-w-xs">
        <NativeSelect aria-label="Status filter" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="appts-status-filter"><option value="">All statuses</option>{APPT_STATUSES.map((s) => <option key={s}>{s}</option>)}</NativeSelect>
      </div>
      <ErrorBox error={error} />
      {loading && !data ? <Loading /> : <ApptTable rows={data || []} onOpen={setOpen} />}
      <AppointmentDialog id={open} onClose={() => setOpen(null)} onChanged={reload} />
    </div>
  );
}

const ApptTable = ({ rows, onOpen }) => (
  <SimpleTable testId="appts-table" rows={rows} onRow={(a) => onOpen(a.id)} rowTestId={(a) => `appt-row-${a.id}`}
    cols={[
      { label: "Date", render: (a) => `${fmtDate(a.date)} ${a.start}` },
      { label: "Customer", render: (a) => <>{a.customer_name}<DemoTag show={a.is_demo} /></> },
      { label: "Type", render: (a) => a.type },
      { label: "Request", render: (a) => a.request_reference },
      { label: "Postcode", render: (a) => a.property?.postcode },
      { label: "Status", render: (a) => <StatusBadge status={a.status} /> },
    ]} />
);

const Chip = ({ a, onOpen }) => (
  <button onClick={() => onOpen(a.id)} data-testid={`cal-appt-${a.id}`}
    className={`mb-1 block w-full truncate rounded border px-1.5 py-0.5 text-left text-[11px] ${toneClass(a.status)} ${a.status === "Requested" ? "border-dashed" : ""}`}>
    {a.start} {a.customer_name}{a.status !== "Confirmed" ? ` · ${a.status}` : ""}
  </button>
);

export function CalendarPage() {
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [open, setOpen] = useState(null);
  const range = useMemo(() => {
    if (view === "day") return [cursor, cursor];
    if (view === "week") return [startOfWeek(cursor, { weekStartsOn: 1 }), endOfWeek(cursor, { weekStartsOn: 1 })];
    return [startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }), endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 })];
  }, [view, cursor]);
  const { data, loading, error, reload } = useFetch(`/admin/appointments?date_from=${toISODate(range[0])}&date_to=${toISODate(range[1])}`);
  const visible = (data || []).filter((a) => !["Cancelled", "Declined"].includes(a.status));
  const byDay = (d) => visible.filter((a) => a.date === toISODate(d));
  const step = (n) => setCursor(view === "month" ? addMonths(cursor, n) : addDays(cursor, n * (view === "week" ? 7 : 1)));
  const days = [];
  for (let d = range[0]; d <= range[1]; d = addDays(d, 1)) days.push(d);
  const title = view === "month" ? format(cursor, "MMMM yyyy") : view === "week" ? `Week of ${fmtDate(toISODate(range[0]))}` : format(cursor, "EEEE dd/MM/yyyy");
  return (
    <div>
      <PageHeader title="Calendar" subtitle="Dashed entries are customer requests awaiting confirmation." />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" onClick={() => step(-1)} aria-label="Previous" data-testid="cal-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
          <Button size="sm" variant="outline" onClick={() => setCursor(new Date())} data-testid="cal-today-btn">Today</Button>
          <Button size="icon" variant="outline" onClick={() => step(1)} aria-label="Next" data-testid="cal-next-btn"><ChevronRight className="h-4 w-4" /></Button>
          <h2 className="ml-2 font-heading text-lg font-semibold" data-testid="cal-title">{title}</h2>
        </div>
        <div className="flex rounded-md border border-border bg-card p-0.5" role="tablist">
          {["month", "week", "day", "list"].map((v) => (
            <button key={v} role="tab" aria-selected={view === v} onClick={() => setView(v)} data-testid={`cal-view-${v}`}
              className={`rounded px-3 py-1.5 text-sm capitalize ${view === v ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>{v}</button>
          ))}
        </div>
      </div>
      <ErrorBox error={error} />
      {loading && !data ? <Loading /> : view === "list" ? <ApptTable rows={data || []} onOpen={setOpen} /> : view === "month" ? (
        <div className="overflow-x-auto"><div className="grid min-w-[700px] grid-cols-7 overflow-hidden rounded-lg border border-border bg-card" data-testid="cal-month-grid">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="border-b border-border bg-muted p-2 text-xs font-semibold uppercase text-muted-foreground">{d}</div>)}
          {days.map((d) => (
            <div key={d.toISOString()} className={`min-h-[96px] border-b border-r border-border p-1.5 ${isSameMonth(d, cursor) ? "" : "bg-muted/40 text-muted-foreground"}`}>
              <button className="mb-1 text-xs font-medium hover:underline" onClick={() => { setCursor(d); setView("day"); }}>{format(d, "d")}</button>
              {byDay(d).map((a) => <Chip key={a.id} a={a} onOpen={setOpen} />)}
            </div>))}
        </div></div>
      ) : (
        <div className={`grid gap-3 ${view === "week" ? "md:grid-cols-7" : ""}`} data-testid={`cal-${view}-grid`}>
          {days.map((d) => (
            <div key={d.toISOString()} className="rounded-lg border border-border bg-card p-2">
              <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">{format(d, "EEE dd/MM")}</p>
              {byDay(d).length ? byDay(d).map((a) => (
                <button key={a.id} onClick={() => setOpen(a.id)} data-testid={`cal-appt-${a.id}`} className={`mb-2 block w-full rounded border p-2 text-left text-xs ${toneClass(a.status)} ${a.status === "Requested" ? "border-dashed" : ""}`}>
                  <span className="font-semibold">{a.start}–{a.end}</span><br />{a.customer_name}<br />{a.type}<br /><span className="font-medium">{a.status}</span>
                </button>)) : <p className="text-xs text-muted-foreground">No appointments</p>}
            </div>))}
        </div>
      )}
      <AppointmentDialog id={open} onClose={() => setOpen(null)} onChanged={reload} />
    </div>
  );
}
